/**
 * Encrypted token storage utility
 * Provides secure storage for authentication tokens in localStorage
 */
import { encrypt, decrypt } from './crypto';

const SESSION_KEY = 'bic_token';

/**
 * Store encrypted token in localStorage
 * @param {string} token - The token to encrypt and store
 */
export const setEncryptedToken = (token) => {
  if (!token) {
    localStorage.removeItem(SESSION_KEY);
    return;
  }

  try {
    const encryptedToken = encrypt(token);
    localStorage.setItem(SESSION_KEY, encryptedToken);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to encrypt token:', error);
    // Fallback to unencrypted storage if encryption fails
    localStorage.setItem(SESSION_KEY, token);
  }
};

/**
 * Retrieve and decrypt token from localStorage
 * @returns {string|null} - Decrypted token or null if not found
 */
export const getEncryptedToken = () => {
  try {
    const storedValue = localStorage.getItem(SESSION_KEY);
    if (!storedValue) {
      return null;
    }

    // Try to decrypt (handles both encrypted and legacy unencrypted tokens)
    try {
      const decrypted = decrypt(storedValue);
      // If decryption returns the same value, it might be unencrypted
      // Check if it looks like a JWT token (contains dots)
      if (decrypted === storedValue && storedValue.includes('.')) {
        // Likely an unencrypted token, migrate it
        setEncryptedToken(storedValue);
        return storedValue;
      }
      return decrypted || storedValue;
    } catch {
      // If decryption fails, assume it's an unencrypted legacy token
      // Migrate it to encrypted storage
      setEncryptedToken(storedValue);
      return storedValue;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to retrieve token:', error);
    return null;
  }
};

/**
 * Remove token from localStorage
 */
export const removeEncryptedToken = () => {
  localStorage.removeItem(SESSION_KEY);
};

/**
 * Check if token exists in localStorage
 * @returns {boolean}
 */
export const hasEncryptedToken = () => !!localStorage.getItem(SESSION_KEY);
